import React, { useState, useEffect } from 'react';
import { flashNewsService, type FlashNews as FlashNewsType } from '../services';

const FlashNews: React.FC = () => {
  const [newsItems, setNewsItems] = useState<FlashNewsType[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Load flash news from service
  useEffect(() => {
    const loadFlashNews = async () => {
      try {
        const response = await flashNewsService.getActiveFlashNews();
        if (response.success && response.data.length > 0) {
          setNewsItems(response.data);
        }
      } catch {
        console.error('Error occurred');
      }
    };

    loadFlashNews();
  }, []);

  // Auto-rotate news items
  useEffect(() => {
    if (newsItems.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % newsItems.length);
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [newsItems.length]);


  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + newsItems.length) % newsItems.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % newsItems.length);
  };

  if (newsItems.length === 0) return null;

  const currentNews = newsItems[currentIndex];

  const getBackgroundColor = (type: FlashNewsType['type']) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
    case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'alert':
        return 'bg-accent-50 border-accent-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const getTextColor = (type: FlashNewsType['type']) => {
    switch (type) {
      case 'success':
        return 'text-green-800';
      case 'warning':
        return 'text-yellow-800';
      case 'alert':
        return 'text-accent-800';
      default:
        return 'text-blue-800';
    }
  };

  const getIconColor = (type: FlashNewsType['type']) => {
    switch (type) {
      case 'success':
        return 'text-green-600';
      case 'warning':
        return 'text-yellow-600';
      case 'alert':
        return 'text-accent-600';
      default:
        return 'text-blue-600';
    }
  };

  return (
    <div className={`relative ${getBackgroundColor(currentNews.type)} border-b transition-all duration-300 animate-slide-down`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3">
          {/* Navigation for multiple news items */}
          {newsItems.length > 1 && (
            <button
              onClick={handlePrevious}
              className={`p-1 rounded-full hover:bg-white/50 transition-colors ${getIconColor(currentNews.type)}`}
              aria-label="Previous news"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* News content */}
          <div className="flex-1 flex items-center justify-center gap-3 px-4">
            {/* Animated icon */}
            <div className={`${getIconColor(currentNews.type)} animate-pulse`}>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
            </div>

            {/* Message with animation */}
            <div className="overflow-hidden">
              <p className={`text-sm sm:text-base font-medium ${getTextColor(currentNews.type)} inline-flex flex-wrap items-center`}>
                {currentNews.message.split('').map((char, index) => (
                  <span
                    key={index}
                    className={`inline-block ${char === ' ' ? 'w-1' : ''} animate-wave`}
                    style={{
                      animationDelay: `${index * 30}ms`,
                      animationDuration: '2s'
                    }}
                  >
                    {char === ' ' ? '\u00A0' : char}
                  </span>
                ))}
              </p>
            </div>

            {/* Optional link */}
            {currentNews.link && (
              <a
                href={currentNews.link.url}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/80 hover:bg-white transition-colors text-sm font-semibold ${getTextColor(currentNews.type)} hover:scale-105 transform transition-transform`}
              >
                {currentNews.link.text}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            )}
          </div>

          {/* Navigation and close */}
          <div className="flex items-center gap-2">
            {newsItems.length > 1 && (
              <>
                <button
                  onClick={handleNext}
                  className={`p-1 rounded-full hover:bg-white/50 transition-colors ${getIconColor(currentNews.type)}`}
                  aria-label="Next news"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {/* Dots indicator */}
                <div className="hidden sm:flex items-center gap-1 mx-2">
                  {newsItems.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                        index === currentIndex
                          ? `${getIconColor(currentNews.type).replace('text-', 'bg-')} w-6`
                          : 'bg-neutral-400'
                      }`}
                      aria-label={`Go to news ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlashNews;